import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"

import useSolarProposalPricing from "~/api/hooks/useSolarProposalPricing"
import useSolarProposalSavings from "~/api/hooks/useSolarProposalSavings"
import useUpdateSolarProduction from "~/api/hooks/useUpdateSolarProduction"
import { parseAxiosError } from "~/utils/parseAxiosError"

import { OutOfDateNotice } from "../ProposalPresentation/pricing/pricing-out-of-date"

export const SOLAR_DESIGN_PRODUCTION_WARNING = `Saved, but production couldn't be updated`

const primaryButtonClass = `inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50`

/** What a design change left out of date, read back from the refetched reads. */
export const designChangeOutOfDateLabel = (stale: {
  pricing: boolean
  savings: boolean
}): string | null =>
  stale.pricing && stale.savings
    ? `Pricing and savings are now out of date`
    : stale.pricing
      ? `Pricing is now out of date`
      : stale.savings
        ? `Savings are now out of date`
        : null

/**
 * After a design change (a designer save, an Aurora switch or refresh): pricing
 * and savings left for someone to refresh on purpose, and production when the
 * gateway couldn't update it, with a retry through `POST …/production`. The
 * retry refetches the design read, whose production status then clears.
 */
export const SolarDesignChangeOutcome = ({
  proposalId,
  productionNotUpdated,
  onProductionUpdated,
  testId,
}: {
  proposalId: string
  productionNotUpdated: boolean
  /** A retry succeeded. */
  onProductionUpdated?: () => void
  testId: string
}): JSX.Element | null => {
  const pricing = useSolarProposalPricing(proposalId)
  const savings = useSolarProposalSavings(proposalId)
  const production = useUpdateSolarProduction()
  const outOfDate = designChangeOutOfDateLabel({
    pricing: pricing.data?.pricingStatus === `out_of_date`,
    savings: savings.data?.savingsStatus === `out_of_date`,
  })

  if (!outOfDate && !productionNotUpdated) return null

  return (
    <div className="flex flex-col gap-3">
      {outOfDate ? <OutOfDateNotice label={outOfDate} testId={testId} /> : null}
      {productionNotUpdated ? (
        <div
          role="alert"
          className="space-y-3 rounded-xl border border-warning/40 bg-warning/10 p-4"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <ExclamationTriangleIcon
              aria-hidden="true"
              className="size-5 text-warning"
            />
            {SOLAR_DESIGN_PRODUCTION_WARNING}
          </p>
          {production.isError ? (
            <p className="text-sm text-danger">
              {parseAxiosError(
                production.error,
                `Production couldn't be updated. Try again.`,
              )}
            </p>
          ) : null}
          <button
            type="button"
            className={primaryButtonClass}
            disabled={production.isLoading}
            onClick={() =>
              production.mutate(
                { proposalId },
                { onSuccess: onProductionUpdated },
              )
            }
          >
            {production.isLoading ? `Retrying…` : `Retry production`}
          </button>
        </div>
      ) : null}
    </div>
  )
}
